import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { env } from "../lib/env";

/**
 * Server-authoritative chain/contract configuration. Contract addresses, fee bps, and the
 * minimum listing price are never hardcoded in this app — they always come from here.
 */
export function usePublicConfig() {
  return useQuery({
    queryKey: queryKeys.publicConfig,
    queryFn: ({ signal }) => apiClient.getPublicConfig(signal),
    staleTime: 5 * 60_000,
    gcTime: 60 * 60_000,
  });
}

export interface NetworkCheck {
  /** True only when the build's VITE_BASE_CHAIN_ID matches the server's authoritative chainId. */
  matches: boolean;
  buildChainId: number;
  serverChainId: number | null;
}

/**
 * Cross-checks the build-time `VITE_BASE_CHAIN_ID` against the server's `GET /config/public`
 * `chainId`, per Build.md: "The UI must display the active network and refuse checkout on
 * the wrong network." A mismatch here means this deployment is misconfigured (wrong env
 * var for this environment), not a wallet-level network issue — Smart Account calls specify
 * their network per-call rather than requiring a wallet "switch network" action.
 */
export function useNetworkCheck(): NetworkCheck {
  const { data } = usePublicConfig();
  return {
    matches: data ? data.chainId === env.VITE_BASE_CHAIN_ID : false,
    buildChainId: env.VITE_BASE_CHAIN_ID,
    serverChainId: data?.chainId ?? null,
  };
}
